import * as types from "./types";

// Define VAPI states constants to match spacesVapiService.js
const VAPI_STATES = {
  IDLE: "idle",
  INITIALIZING: "initializing",
  READY: "ready",
  CONNECTING: "connecting",
  ACTIVE: "active",
  ENDING: "ending",
  CLEANING: "cleaning",
  ERROR: "error",
};

/**
 * Redux middleware to handle VAPI background message actions
 * This middleware intercepts VAPI_SEND_BACKGROUND_MESSAGE actions and uses the vapiService
 * to send messages during active calls
 */
export const vapiMiddleware = (store) => (next) => (action) => {
  // Process the action first so the state updates
  const result = next(action);

  // Handle the VAPI SEND_BACKGROUND_MESSAGE action
  if (action.type === types.VAPI_SEND_BACKGROUND_MESSAGE) {
    const { message, type } = action.payload;
    const state = store.getState();
    const isCallActive = state.ai?.vapi?.isCallActive;
    const currentVapiState = window.spacesVapiState?.state;

    // Only send messages during active calls and when in ACTIVE state
    if (isCallActive && message && currentVapiState === VAPI_STATES.ACTIVE) {
      const vapiInstance = window.spacesVapiState?.activeInstance;

      if (vapiInstance) {
        try {
          console.info("Sending background message to VAPI:", message);
          const content =
            type === "system"
              ? message
              : `User just sent a new message via chat: ${message}`;

          vapiInstance.send({
            type: "add-message",
            message: {
              role: "system",
              content,
            },
          });
        } catch (err) {
          console.error("Error sending background message to VAPI:", err);
        }
      } else {
        console.warn(
          "No active VAPI instance found to send background message"
        );
      }
    } else if (isCallActive && currentVapiState !== VAPI_STATES.ACTIVE) {
      console.warn(
        `Cannot send background message in state: ${currentVapiState}`
      );
    }
  }
  // Handle other VAPI actions
  switch (action.type) {
    case types.VAPI_SAY: {
      const { message, endCallAfterSpoken } = action.payload;
      const state = store.getState();
      const isCallActive = state.ai?.vapi?.isCallActive;
      const currentVapiState = window.spacesVapiState?.state;

      // Only execute say during active calls and when in ACTIVE state
      if (isCallActive && message && currentVapiState === VAPI_STATES.ACTIVE) {
        const vapiInstance = window.spacesVapiState?.activeInstance;

        if (vapiInstance) {
          try {
            console.info("Making VAPI assistant say:", message);
            vapiInstance.say(message, endCallAfterSpoken);
          } catch (err) {
            console.error("Error making VAPI assistant speak:", err);
          }
        } else {
          console.warn("No active VAPI instance found for say command");
        }
      } else if (isCallActive && currentVapiState !== VAPI_STATES.ACTIVE) {
        console.warn(
          `Cannot execute say command in state: ${currentVapiState}`
        );
      }
      break;
    }

    case types.VAPI_SAY_AND_MUTE_AFTER: {
      const { message, endCallAfterSpoken } = action.payload;
      const state = store.getState();
      const isCallActive = state.ai?.vapi?.isCallActive;
      const currentVapiState = window.spacesVapiState?.state;

      // Only execute say during active calls and when in ACTIVE state
      if (isCallActive && message && currentVapiState === VAPI_STATES.ACTIVE) {
        const vapiInstance = window.spacesVapiState?.activeInstance;

        if (vapiInstance) {
          try {
            console.info(
              "owo Making VAPI assistant say (will mute after):",
              message
            );
            vapiInstance.say(message, endCallAfterSpoken);
            // The mute will be triggered by the speech-end event in the VAPI service
          } catch (err) {
            console.error("Error making VAPI assistant speak:", err);
          }
        } else {
          console.warn(
            "No active VAPI instance found for say and mute command"
          );
        }
      } else if (isCallActive && currentVapiState !== VAPI_STATES.ACTIVE) {
        console.warn(
          `Cannot execute say and mute command in state: ${currentVapiState}`
        );
      }
      break;
    }

    case types.VAPI_MUTE_ASSISTANT: {
      const state = store.getState();
      const isCallActive = state.ai?.vapi?.isCallActive;
      const currentVapiState = window.spacesVapiState?.state;

      if (isCallActive && currentVapiState === VAPI_STATES.ACTIVE) {
        const vapiInstance = window.spacesVapiState?.activeInstance;

        if (vapiInstance) {
          try {
            console.info("owo Muting VAPI assistant");
            vapiInstance.send({
              type: "control",
              control: "mute-assistant",
            });
          } catch (err) {
            console.error("Error muting VAPI assistant:", err);
          }
        } else {
          console.warn("No active VAPI instance found for assistant mute");
        }
      } else if (isCallActive && currentVapiState !== VAPI_STATES.ACTIVE) {
        console.warn(`Cannot mute assistant in state: ${currentVapiState}`);
      }
      break;
    }

    case types.VAPI_UNMUTE_ASSISTANT: {
      const state = store.getState();
      const isCallActive = state.ai?.vapi?.isCallActive;
      const currentVapiState = window.spacesVapiState?.state;

      if (isCallActive && currentVapiState === VAPI_STATES.ACTIVE) {
        const vapiInstance = window.spacesVapiState?.activeInstance;

        if (vapiInstance) {
          try {
            console.info("Unmuting VAPI assistant");
            vapiInstance.send({
              type: "control",
              control: "unmute-assistant",
            });
          } catch (err) {
            console.error("Error unmuting VAPI assistant:", err);
          }
        } else {
          console.warn("No active VAPI instance found for assistant unmute");
        }
      } else if (isCallActive && currentVapiState !== VAPI_STATES.ACTIVE) {
        console.warn(`Cannot unmute assistant in state: ${currentVapiState}`);
      }
      break;
    }

    case types.VAPI_TOGGLE_ASSISTANT_MUTE: {
      const state = store.getState();
      const isCallActive = state.ai?.vapi?.isCallActive;
      const isAssistantMuted = state.ai?.vapi?.isAssistantMuted;
      const currentVapiState = window.spacesVapiState?.state;

      if (isCallActive && currentVapiState === VAPI_STATES.ACTIVE) {
        const vapiInstance = window.spacesVapiState?.activeInstance;

        if (vapiInstance) {
          try {
            const targetControl = isAssistantMuted
              ? "unmute-assistant"
              : "mute-assistant";
            console.info(
              `${isAssistantMuted ? "Unmuting" : "Muting"} VAPI assistant`
            );

            vapiInstance.send({
              type: "control",
              control: targetControl,
            });
          } catch (err) {
            console.error("Error toggling VAPI assistant mute:", err);
          }
        } else {
          console.warn(
            "No active VAPI instance found for assistant mute toggle"
          );
        }
      } else if (isCallActive && currentVapiState !== VAPI_STATES.ACTIVE) {
        console.warn(
          `Cannot toggle assistant mute in state: ${currentVapiState}`
        );
      }
      break;
    }

    default:
      break;
  }

  return result;
};

export default vapiMiddleware;
